import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Sparkles, Lock, PlayCircle } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Markdown } from '@/client/components/cms/Markdown';
import { Seo } from '@/client/components/Seo';
import { api, ApiError } from '@/client/lib/api';
import { isLocale, type Locale } from '@/client/i18n';
import { PRODUCTION_ORIGIN } from '@/client/lib/site';
import type { PublicCourseDetailResponse } from '@/client/types/courses';
import { IntroVideoPlayer } from '@/client/features/courses/IntroVideoPlayer';
import { PublicCourseReviews } from '@/client/features/reviews/PublicCourseReviews';
import { StarRatingDisplay } from '@/client/features/reviews/StarRating';

export function CourseDetail() {
  const { t } = useTranslation();
  const params = useParams();
  const slug = params.slug ?? '';
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  const query = useQuery({
    queryKey: ['public', 'course', slug],
    queryFn: () => api<PublicCourseDetailResponse>(`/api/v1/courses/${slug}`),
    enabled: Boolean(slug),
    retry: false,
  });

  if (query.isLoading) {
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-text-secondary">
        <div className="inline-flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <h1 className="font-display text-h2 text-primary-dark">{t('errors.not_found_title')}</h1>
          <Link
            to={`/${locale}/kurzy`}
            className="mt-6 inline-flex items-center gap-2 font-sans text-body text-primary hover:gap-3 transition-all"
          >
            <ArrowLeft size={16} /> {t('courses.page_title')}
          </Link>
        </div>
      );
    }
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-error">
        {t('errors.generic')}
      </div>
    );
  }

  if (!query.data) return null;

  const { course, episodes, enrolled } = query.data;
  const hasLaunch =
    course.launchPriceCents !== null &&
    course.launchSeatsTotal > 0 &&
    course.launchSeatsRemaining > 0;

  const formatter = new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: course.currency,
    minimumFractionDigits: 0,
  });

  const offerPriceCents =
    hasLaunch && course.launchPriceCents !== null ? course.launchPriceCents : course.priceCents;
  const offerPrice = (offerPriceCents / 100).toFixed(2);
  const courseJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.subtitle ?? course.promise,
    inLanguage: 'sk',
    provider: {
      '@type': 'Organization',
      name: 'Akadémia Dajany Rodriguez',
      sameAs: 'https://dajanarodriguez.com',
    },
    offers: {
      '@type': 'Offer',
      price: offerPrice,
      priceCurrency: course.currency,
      availability: 'https://schema.org/InStock',
      url: `${PRODUCTION_ORIGIN}/${locale}/kurzy/${course.slug}`,
    },
    ...(course.avgRating !== null && course.reviewsCount > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: course.avgRating.toFixed(1),
            reviewCount: course.reviewsCount,
            bestRating: '5',
            worstRating: '1',
          },
        }
      : {}),
  };

  const seoDescription = course.subtitle ?? course.promise;

  return (
    <article>
      <Seo
        title={`${course.title} — ${t('seo.courses_title')}`}
        description={seoDescription}
        locale={locale}
        ogImage={course.heroUrl ?? undefined}
        jsonLd={courseJsonLd}
      />

      {/* HERO */}
      <section className="relative">
        <div
          className="absolute top-10 -left-32 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div
          className="absolute top-40 -right-32 w-[32rem] h-[32rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(117,59,189,0.20) 0%, transparent 65%)',
            filter: 'blur(48px)',
            animationDelay: '-12s',
          }}
          aria-hidden
        />

        <div className="relative mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-12 lg:pb-20">
          <Link
            to={`/${locale}/kurzy`}
            className="inline-flex items-center gap-2 font-sans text-body-small text-primary hover:gap-3 transition-all animate-fade-up"
          >
            <ArrowLeft size={14} /> {t('courses.page_title')}
          </Link>

          <div className="mt-6 grid gap-10 lg:gap-14 md:grid-cols-[1.1fr_1fr] items-center">
            <div>
              {hasLaunch && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-pill px-3 py-1 font-sans text-caption font-medium text-white animate-fade-up"
                  style={{
                    background: 'linear-gradient(135deg, #753bbd, #512d6d)',
                    boxShadow: '0 4px 14px rgba(117,59,189,0.45)',
                  }}
                >
                  <Sparkles size={11} />
                  {course.launchSeatsRemaining === 1
                    ? t('courses.launch_seats_remaining_one')
                    : t('courses.launch_seats_remaining', {
                        count: course.launchSeatsRemaining,
                        total: course.launchSeatsTotal,
                      })}
                </span>
              )}

              <h1
                className="mt-4 font-display text-[clamp(2.25rem,5.5vw,3.75rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
                style={{ animationDelay: '70ms' }}
              >
                {course.title}
              </h1>
              {course.subtitle && (
                <p
                  className="mt-3 font-display text-body-large text-text-secondary leading-relaxed animate-fade-up"
                  style={{ animationDelay: '140ms' }}
                >
                  {course.subtitle}
                </p>
              )}
              <p
                className="mt-6 font-display text-body-large italic text-primary leading-relaxed animate-fade-up"
                style={{ animationDelay: '210ms' }}
              >
                “{course.promise}”
              </p>

              <div
                className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 animate-fade-up"
                style={{ animationDelay: '280ms' }}
              >
                {course.durationLabel && (
                  <span className="inline-flex items-center gap-1.5 font-sans text-body-small text-text-secondary">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {t('courses.duration_label', { label: course.durationLabel })}
                  </span>
                )}
                {course.avgRating !== null && course.reviewsCount > 0 && (
                  <span className="inline-flex items-center gap-2">
                    <StarRatingDisplay value={course.avgRating} size={16} />
                    <span className="font-sans text-body-small text-text-secondary">
                      {course.avgRating.toFixed(1)}{' '}
                      {t('courses.reviews_count_label', { count: course.reviewsCount })}
                    </span>
                  </span>
                )}
              </div>

              <div
                className="mt-8 dr-card-static p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5 animate-fade-up"
                style={{ animationDelay: '350ms' }}
              >
                <div className="flex-1">
                  {hasLaunch && course.launchPriceCents !== null ? (
                    <>
                      <p className="font-sans text-caption uppercase tracking-[0.14em] text-primary/80">
                        {t('courses.price_from')}
                      </p>
                      <p className="font-display text-[2.25rem] leading-none text-primary-dark">
                        {formatter.format(course.launchPriceCents / 100)}
                      </p>
                      <p className="mt-1 font-sans text-body-small text-text-muted">
                        {t('courses.price_regular')}{' '}
                        <span className="line-through">
                          {formatter.format(course.priceCents / 100)}
                        </span>
                      </p>
                    </>
                  ) : (
                    <p className="font-display text-[2.25rem] leading-none text-primary-dark">
                      {formatter.format(course.priceCents / 100)}
                    </p>
                  )}
                </div>
                {enrolled ? (
                  <Link to={`/${locale}/portal/kurzy/${course.slug}`} className="w-full sm:w-auto">
                    <Button size="lg" className="w-full sm:w-auto">
                      {t('courses.continue_cta')}
                      <ArrowRight size={16} />
                    </Button>
                  </Link>
                ) : (
                  <Link to={`/${locale}/checkout?course=${course.slug}`} className="w-full sm:w-auto">
                    <Button size="lg" className="w-full sm:w-auto">
                      {t('courses.buy_cta')}
                      <ArrowRight size={16} />
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            <div
              className="relative animate-fade-up"
              style={{ animationDelay: '420ms' }}
            >
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-primary-light/30 to-primary/10 blur-xl pointer-events-none" aria-hidden />
              <div className="relative rounded-2xl overflow-hidden border border-glass-border-strong shadow-glass-strong bg-white">
                {course.introVideoId ? (
                  <IntroVideoPlayer
                    slug={course.slug}
                    thumbnailUrl={course.heroUrl}
                    title={course.title}
                  />
                ) : course.heroUrl ? (
                  <div className="relative">
                    <img
                      src={course.heroUrl}
                      alt={course.title}
                      className="aspect-[4/3] w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent pointer-events-none" />
                  </div>
                ) : (
                  <div
                    className="aspect-[4/3] w-full flex items-center justify-center"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(193,167,226,0.65) 0%, rgba(117,59,189,0.45) 100%)',
                    }}
                  >
                    <PlayCircle size={56} className="text-white/85" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DESCRIPTION */}
      <section className="mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="dr-card-static p-7 sm:p-10 lg:p-12">
          <Markdown source={course.descriptionMd} />
        </div>
      </section>

      {/* EPISODES */}
      {episodes.length > 0 && (
        <section className="mx-auto max-w-4xl px-5 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="dr-eyebrow">
              {t('courses.curriculum_eyebrow', { defaultValue: 'Curriculum' })}
            </p>
            <h2 className="mt-3 font-display text-h2 text-primary-dark">
              {t('courses.what_youll_learn')}
            </h2>
          </div>

          <ol className="mt-12 space-y-3 sm:space-y-4 stagger-children">
            {episodes.map((episode) => (
              <li key={episode.id}>
                <article className="dr-card-static p-5 sm:p-6 flex items-start gap-4 sm:gap-5">
                  <div className="shrink-0 inline-flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 font-display text-h4 text-primary">
                    {episode.displayOrder}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-caption uppercase tracking-[0.14em] text-primary/80">
                      {t('courses.episode_label', { number: episode.displayOrder })}
                    </p>
                    <h3 className="mt-1 font-display text-h4 text-primary-dark">
                      {episode.title}
                    </h3>
                    {episode.descriptionMd && (
                      <p className="mt-2 font-sans text-body text-text-secondary leading-relaxed">
                        {episode.descriptionMd}
                      </p>
                    )}
                    {episode.unlockAfterDays > 0 && (
                      <p className="mt-3 inline-flex items-center gap-1.5 font-sans text-caption text-text-muted">
                        <Lock size={12} />
                        {t('courses.episode_unlocks_in_days', { count: episode.unlockAfterDays })}
                      </p>
                    )}
                  </div>
                </article>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* ABOUT DAJANA */}
      <section className="mx-auto max-w-4xl px-5 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="dr-card-static p-8 sm:p-12 lg:p-16 text-center relative overflow-hidden">
          <div
            className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(193,167,226,0.30) 0%, transparent 65%)',
              filter: 'blur(36px)',
            }}
            aria-hidden
          />
          <p className="dr-eyebrow relative">{t('courses.about_eyebrow', { defaultValue: 'O lektorke' })}</p>
          <h2 className="relative mt-3 font-display text-h2 text-primary-dark">
            {t('courses.about_dajana')}
          </h2>
          <p className="relative mt-5 font-display text-body-large text-text-secondary leading-relaxed max-w-2xl mx-auto">
            {t('landing.section_about_body')}
          </p>
          <Link
            to={`/${locale}/o-dajane`}
            className="relative inline-flex items-center gap-2 mt-7 font-sans text-body font-medium text-primary hover:gap-3 transition-all"
          >
            {t('courses.about_dajana_link')}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="dr-card-static p-8 sm:p-10 lg:p-12">
          <PublicCourseReviews slug={course.slug} locale={locale} />
        </div>
      </section>
    </article>
  );
}
